/**
 * useProductData Hook
 * Manages product list fetching and filtering
 * Consolidates duplicate logic from RecipeConfig and RecipeCardView
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { get_product_list_both } from '@/utils/api';
import { filterBySearch } from '@/utils/recipe/recipe.utils';

export const useProductData = (selectedButton, selectedModule, options = {}) => {
  const { autoFetch = true, enableSearch = true } = options;

  // State
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // For search/filter
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Refs
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  /**
   * Fetch products based on recipe status and module type
   */
  const fetchProducts = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        module_type: selectedModule,
        // recipe_type boolean: true (Production) / false (Test).
        recipe_type: selectedButton === 'production',
      };

      await get_product_list_both(requestData, (res) => {
        if (!isMountedRef.current) return;

        if (res?.data?.status === 'success') {
          const productData = res?.data?.data || [];
          setProducts(productData);
          setAllProducts(productData);
          setError(null);
        } else {
          setProducts([]);
          setAllProducts([]);
          setError(res?.data?.message || 'Failed to fetch products');
        }

        setLoading(false);
      });
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMessage = err.message || 'Error fetching products';
      setError(errorMessage);
      setProducts([]);
      setAllProducts([]);
      setLoading(false);
      console.error('Product fetch error:', err);
    }
  }, [selectedButton, selectedModule]);

  /**
   * Search/filter products by name
   */
  const searchProducts = useCallback((value) => {
    setSearchValue(value);

    if (!value || !value.trim()) {
      setProducts(allProducts);
      return;
    }

    const filtered = filterBySearch(allProducts, value, 'product_name');
    setProducts(filtered);
  }, [allProducts]);

  /**
   * Get product by ID
   */
  const getProductById = useCallback((productId) => {
    return allProducts.find(p => p.product_id === productId);
  }, [allProducts]);

  /**
   * Get product name by ID
   */
  const getProductName = useCallback((productId) => {
    const product = getProductById(productId);
    return product?.product_name || '';
  }, [getProductById]);

  /**
   * Refresh products
   */
  const refreshProducts = useCallback(() => {
    return fetchProducts();
  }, [fetchProducts]);

  /**
   * Clear search
   */
  const clearSearch = useCallback(() => {
    setSearchValue('');
    setProducts(allProducts);
  }, [allProducts]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch) {
      fetchProducts();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedButton, selectedModule, autoFetch, fetchProducts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // Data
    products,
    allProducts,
    searchValue,

    // Loading states
    loading,
    error,
    hasProducts: products.length > 0,

    // Actions
    fetchProducts,
    refreshProducts,
    searchProducts,
    clearSearch,

    // Helpers
    getProductById,
    getProductName,

    // Computed values
    totalProducts: allProducts.length,
    displayedProducts: products.length,
  };
};

export default useProductData;
